import React, { useState, useEffect, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import {
  Box,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GetAppIcon from "@mui/icons-material/GetApp";
import PrintIcon from "@mui/icons-material/Print";
import ImageIcon from "@mui/icons-material/Image";
import { useNavigate } from "react-router-dom";
import { useOutletContext } from "react-router-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { URL } from "../config";

const ServiceRequestList = () => {
  const { setLoading } = useOutletContext();
  const navigate = useNavigate();
  const [dataSource, setDataSource] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");

  useEffect(() => {
    
    const fetchServiceRequests = async () => {
      setLoading(true); 
      try {
        const response = await axios.get(`${URL}/servicerequests`);
        const requests = response.data;

        const finalData = await Promise.all(
          requests.map(async (item) => {
            let requestTypeName = "N/A";
            try {
              const rt = await axios.get(`${URL}/requesttypes/${item.fkRequestTypeId}`);
              requestTypeName = rt.data?.RequestTypeName || "N/A";
            } catch (err) {
              console.log("RequestType fetch error:", err);
            }

            return {
              ServiceRequestId: item.ServiceRequestId,
              RequestTypeId: item.fkRequestTypeId,
              RequestTypeName: requestTypeName,
              CustomerName: item.CustomerName || "N/A",
              MobileNo: item.MobileNo || "N/A",
              Address: item.Address || "N/A",
              Latitude: item.Latitude || "-",
              Longitude: item.Longitude || "-",
              PreferredDate: item.PreferredDate || "",
              PreferredTime: item.PreferredTime || "",
              Description: item.Description || "",
              UploadImage: item.UploadImage ? `${URL}/uploads/${item.UploadImage}` : null,
              createdAt: item.createdAt,
            };
          })
        );

        setDataSource(finalData);
      } catch (error) {
        console.error("Error fetching service requests:", error);
      }
      setLoading(false);
    };

    fetchServiceRequests();
    
  }, []);

  const handleEdit = (id) => {
    navigate(`/dashboard/${id}`);
  };

  const handleDownloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Service Requests");

    worksheet.columns = [
      { header: "Request ID", key: "ServiceRequestId", width: 15 },
      { header: "Request Type", key: "RequestTypeName", width: 20 },
      { header: "Customer Name", key: "CustomerName", width: 20 },
      { header: "Mobile No", key: "MobileNo", width: 15 },
      { header: "Address", key: "Address", width: 25 },
      { header: "Latitude", key: "Latitude", width: 15 },
      { header: "Longitude", key: "Longitude", width: 15 },
      { header: "Preferred Date", key: "PreferredDate", width: 18 },
      { header: "Preferred Time", key: "PreferredTime", width: 18 },
      { header: "Description", key: "Description", width: 30 },
    ];

    dataSource.forEach((item) => worksheet.addRow(item));

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      "ServiceRequestList.xlsx"
    );
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    doc.setFontSize(18);
    doc.text("Service Request List", 40, 40);

    const head = [[
      "Request ID",
      "Request Type",
      "Customer Name",
      "Mobile No",
      "Address",
      "Latitude",
      "Longitude",
      "Preferred Date",
      "Preferred Time",
      "Description",
    ]];

    const body = dataSource.map((item) => [
      item.ServiceRequestId,
      item.RequestTypeName,
      item.CustomerName,
      item.MobileNo,
      item.Address,
      item.Latitude,
      item.Longitude,
      item.PreferredDate,
      item.PreferredTime,
      item.Description,
    ]);

    autoTable(doc, {
      startY: 60,
      head,
      body,
      headStyles: { fillColor: [23, 66, 83], textColor: "#ffffff" },
      styles: { fontSize: 10, cellPadding: 6 },
      theme: "grid",
    });

    doc.save("ServiceRequestList.pdf");
  };

  const handlePrint = () => {
    const tableRows = dataSource
      .map(
        (item) => `
      <tr>
        <td>${item.ServiceRequestId}</td>
        <td>${item.RequestTypeName}</td>
        <td>${item.CustomerName}</td>
        <td>${item.MobileNo}</td>
        <td>${item.Address}</td>
        <td>${item.Latitude}</td>
        <td>${item.Longitude}</td>
        <td>${item.PreferredDate}</td>
        <td>${item.PreferredTime}</td>
        <td>${item.Description}</td>
      </tr>`
      )
      .join("");

    const style = `
      <style>
        @media print {
          @page { size: landscape; margin: 18mm; }
        }
        table { width: 100%; border-collapse: collapse; font-size: 13px; }
        th { background: #174253; color: #fff; padding: 8px; }
        td { padding: 8px; border-top: 1px solid #ccc; border-bottom: 1px solid #ccc; }
        tr:nth-child(even) td { background: #f5f5f5; }
        h1 { text-align: center; color: #174253; margin-bottom: 20px; }
      </style>
    `;

    const html = `
      <html>
        <head>${style}</head>
        <body>
          <h1>Service Request List</h1>
          <table>
            <thead>
              <tr>
                <th>Request ID</th>
                <th>Request Type</th>
                <th>Customer Name</th>
                <th>Mobile No</th>
                <th>Address</th>
                <th>Latitude</th>
                <th>Longitude</th>
                <th>Preferred Date</th>
                <th>Preferred Time</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>${tableRows}</tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "Actions",
        header: "Actions",
        size: 60,
        Cell: ({ row }) => (
          <IconButton
            size="small"
            onClick={() => handleEdit(row.original.ServiceRequestId)}
          >
            <EditIcon sx={{ color: "#174253" }} fontSize="small" />
          </IconButton>
        ),
      },
      { accessorKey: "ServiceRequestId", header: "Request ID", size: 100 },
      { accessorKey: "RequestTypeName", header: "Request Type", size: 140 },
      { accessorKey: "CustomerName", header: "Customer Name", size: 160 },
      { accessorKey: "MobileNo", header: "Mobile No", size: 140 },
      { accessorKey: "Address", header: "Address", size: 220 },
      { accessorKey: "Latitude", header: "Latitude", size: 120 },
      { accessorKey: "Longitude", header: "Longitude", size: 120 },
      {
        accessorKey: "PreferredDate",
        header: "Preferred Date",
        size: 130,
      },
      {
        accessorKey: "PreferredTime",
        header: "Preferred Time",
        size: 130,
      },
      { accessorKey: "Description", header: "Description", size: 250 },
      {
        accessorKey: "UploadImage",
        header: "Image",
        size: 100,
        Cell: ({ cell }) => {
          const img = cell.getValue();
          if (!img)
            return (
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "5px",
                  border: "1px solid #ccc",
                  backgroundColor: "#f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ImageIcon sx={{ fontSize: 24, color: "#9e9e9e" }} />
              </Box>
            );

          return (
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: "5px",
                border: "1px solid #ccc",
                overflow: "hidden",
              }}
            >
              <img
                src={img}
                alt="Uploaded"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
          );
        },
      },
    ],
    []
  );

  return (
   <Box sx={{ width: "100%", bgcolor: "#ffffff", marginTop:"20px", p: { xs: 1, md: 0 }, overflow: "hidden" }}>
         
         <Box sx={{ fontWeight: 700, fontSize: "22px", color: "#174253", letterSpacing: 0.5, borderLeft: "5px solid #174253", paddingLeft: "12px", marginBottom:"20px" }}>
           Service Request List
         </Box>
   
         <Box sx={{ background: "#fff", borderRadius: "0 0 15px 15px" }}>
           <div style={{ position: "relative", width: "100%", marginTop:"10px" }}>
             <div style={{ position: "absolute", top: "10px", right: "20px", left: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", zIndex: 2, marginBottom: "10px" }}>
             
               <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                 <Tooltip title="Download Excel">
                   <IconButton onClick={handleDownloadExcel} sx={{ backgroundColor: "#d4f1d4", color: "#174253", "&:hover": { backgroundColor: "#b5e3b5" } }}>
                     <GetAppIcon />
                   </IconButton>
                 </Tooltip>
                 <Tooltip title="Download PDF">
                   <IconButton onClick={handleDownloadPDF} sx={{ backgroundColor: "#ffe2e2", color: "#d32f2f", "&:hover": { backgroundColor: "#ffcccc" } }}>
                     <PictureAsPdfIcon />
                   </IconButton>
                 </Tooltip>
                 <Tooltip title="Print">
                   <IconButton onClick={handlePrint} sx={{ backgroundColor: "#e3f2fd", color: "#174253", "&:hover": { backgroundColor: "#cde4fa" } }}>
                     <PrintIcon />
                   </IconButton>
                 </Tooltip>
               </div>
   
           
               <TextField
                 size="small"
                 placeholder="Search requests..."
                 value={globalFilter}
                 onChange={(e) => setGlobalFilter(e.target.value)}
                 InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#174253" }} /></InputAdornment> }}
                 sx={{ width: { xs: "200px", sm: "250px" }, "& .MuiOutlinedInput-root": { borderRadius: "25px", height: "38px", backgroundColor: "#fff", boxShadow: "0 0 6px rgba(0,0,0,0.1)", "& fieldset": { borderColor: "#d0d7de" }, "&:hover fieldset": { borderColor: "#174253" } } }}
               />
             </div>
           </div>
   
           <MaterialReactTable
             columns={columns}
             data={dataSource.filter(item => Object.values(item).join(" ").toLowerCase().includes(globalFilter.toLowerCase()))}
             enableColumnActions={false}
             enableDensityToggle={false}
             enableFullScreenToggle={false}
             enableColumnFilters={false}
             enableHiding={false}
             enableGlobalFilter={false}
             muiTablePaperProps={{ sx: { boxShadow: "none", background: "#fff", borderRadius: "15px" } }}
             muiTableContainerProps={{ sx: { backgroundColor: "#fff", width: "100%", overflowX: "auto", borderRadius: "12px", "&::-webkit-scrollbar": { height: "6px" }, "&::-webkit-scrollbar-thumb": { backgroundColor: "#e0e3ea", borderRadius: "10px" } } }}
             muiTableHeadCellProps={{ sx: { backgroundColor: "#e0e0e0ff", color: "#393e4f", fontWeight: 600, fontSize: "16px", borderBottom: "0.5px solid #e3e5eb", borderRight: "none", borderLeft: "none", textAlign: "left", height: "63px", paddingTop: "23px", marginTop: "15px" } }}
             muiTableBodyRowProps={{ sx: { backgroundColor: "#fff", "&:hover": { backgroundColor: "#eff6fe" }, "& td": { fontSize: { xs: "12px", sm: "14px" }, p: "8px", borderBottom: "0.5px solid #e3e5eb", borderRight: "none", borderLeft: "none", height: "54px" } } }}
           />
         </Box>
       </Box>
  );
};

export default ServiceRequestList;
